/**
 * useSelectedConversation Hook
 *
 * Handles the currently selected conversation for viewing in center panel.
 * Syncs selection with URL query params (?receiver=xxx) for deep linking.
 *
 * The receiver param supports both formats:
 * - Phone number (e.g., +61412345678) - V2 format
 * - Conversation ID (e.g., 507f1f77bcf86cd799439011) - V3 format
 *
 * The hook auto-detects the format for backward compatibility.
 */

import {useState, useCallback, useEffect} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {ConversationData} from '../types/inbox-v3.types'
import {useInboxResponsive} from '../../inbox-v2/useInboxResponsive'
import {getReceiverParam, findConversationByReceiver} from '../services/inboxMapper'

type MobileView = 'list' | 'chat' | 'details'

interface UseSelectedConversationOptions {
  /** List of conversations to search when restoring from URL */
  conversations?: ConversationData[]
  /** Whether to use conversation ID in URL (V3) instead of phone number (V2) */
  preferConversationId?: boolean
}

interface UseSelectedConversationReturn {
  // State
  selectedConversation: ConversationData | null
  selectedReceiver: string | null
  mobileView: MobileView

  // Actions
  selectConversation: (conversation: ConversationData) => void
  clearSelection: () => void
  goBackToList: () => void
  openContactDetails: () => void
  closeContactDetails: () => void
  setMobileView: (view: MobileView) => void

  // URL sync
  receiverFromUrl: string | null
}

export function useSelectedConversation(
  options: UseSelectedConversationOptions = {}
): UseSelectedConversationReturn {
  const {conversations = [], preferConversationId = false} = options
  const {isMobile, isTablet} = useInboxResponsive()
  const isMobileLayout = isMobile || isTablet

  const location = useLocation()
  const navigate = useNavigate()

  const [selectedConversation, setSelectedConversation] = useState<ConversationData | null>(null)
  const [selectedReceiver, setSelectedReceiver] = useState<string | null>(null)
  const [mobileView, setMobileView] = useState<MobileView>('list')

  // Get receiver from URL
  const queryParams = new URLSearchParams(location.search)
  const receiverFromUrl = queryParams.get('receiver')

  // Update URL with receiver param
  const updateUrlWithReceiver = useCallback(
    (receiver: string | null) => {
      const params = new URLSearchParams(location.search)

      if (receiver) {
        params.set('receiver', receiver)
      } else {
        params.delete('receiver')
      }

      const newSearch = params.toString()
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname

      navigate(newUrl, {replace: true})
    },
    [location.pathname, location.search, navigate]
  )

  // Select a conversation and update URL
  const selectConversation = useCallback(
    (conversation: ConversationData) => {
      setSelectedConversation(conversation)
      setSelectedReceiver(conversation.receiver)

      // Update URL with receiver param using mapper
      // Uses conversation ID if preferConversationId is true, otherwise phone number
      const receiverParam = getReceiverParam(conversation, preferConversationId)
      updateUrlWithReceiver(receiverParam)

      // On mobile/tablet, switch to chat view
      if (isMobileLayout) {
        setMobileView('chat')
      }
    },
    [isMobileLayout, preferConversationId, updateUrlWithReceiver]
  )

  // Clear selection and remove from URL
  const clearSelection = useCallback(() => {
    setSelectedConversation(null)
    setSelectedReceiver(null)
    updateUrlWithReceiver(null)
  }, [updateUrlWithReceiver])

  // Mobile navigation
  const goBackToList = useCallback(() => {
    setMobileView('list')
  }, [])

  const openContactDetails = useCallback(() => {
    setMobileView('details')
  }, [])

  const closeContactDetails = useCallback(() => {
    setMobileView('chat')
  }, [])

  // Restore selection from URL when conversations load
  useEffect(() => {
    // Skip if no receiver in URL or conversations not loaded yet
    if (!receiverFromUrl || conversations.length === 0) return

    // Skip if already have the correct conversation selected
    if (selectedConversation) {
      const currentReceiverParam = getReceiverParam(selectedConversation, preferConversationId)
      // Also check phone number for backward compatibility
      if (
        currentReceiverParam === receiverFromUrl ||
        selectedConversation.receiver === receiverFromUrl ||
        selectedConversation._id === receiverFromUrl
      ) {
        return
      }
    }

    // Find conversation by receiver using mapper
    // This auto-detects if receiverFromUrl is a phone number or conversation ID
    const conversation = findConversationByReceiver(conversations, receiverFromUrl)

    if (conversation) {
      setSelectedConversation(conversation)
      setSelectedReceiver(conversation.receiver)

      // On mobile/tablet, switch to chat view
      if (isMobileLayout) {
        setMobileView('chat')
      }
    }
  }, [receiverFromUrl, conversations, preferConversationId, selectedConversation, isMobileLayout])

  return {
    // State
    selectedConversation,
    selectedReceiver,
    mobileView,

    // Actions
    selectConversation,
    clearSelection,
    goBackToList,
    openContactDetails,
    closeContactDetails,
    setMobileView,

    // URL sync
    receiverFromUrl,
  }
}